/**
 * DealPipelineTabs - Tabs de pipelines do CRM
 * 
 * Permite navegar entre diferentes pipelines e criar/editar/excluir pipelines
 * Inspirado no KanbanCategoryTabs do módulo de conversas
 * 
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v1.3.0 - Alça de arraste (6 pontinhos) nas abas, padrão
 *            visual igual ao de fases e cards.
 *   RELEASE: 2026-08-28 v1.2.0 - Abas passam a ser reordenáveis por drag and drop
 *            (dnd-kit), persistindo a nova ordem via prop `onReorderPipelines`.
 *   v1.1.0 - Faixa rolável (ScrollableTabsBar)
 *   v1.0.0 - Versão inicial
 */

import { useState, useEffect } from 'react';
import { Plus, MoreHorizontal, Pencil, Trash2, Lock, Settings, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsBar } from '@/components/ui/scrollable-tabs-bar';
// Drag and drop das abas (mesmo padrão do PipelinePhaseManager)
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// Cores pré-definidas para pipelines (mesmas do KanbanCategoryTabs)
const PRESET_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

// Interface do Pipeline para o componente
export interface PipelineTab {
  id: string;
  name: string;
  color: string;
  is_default: boolean;
}

interface DealPipelineTabsProps {
  pipelines: PipelineTab[];
  selectedPipelineId: string | null;
  onSelectPipeline: (id: string) => void;
  onCreatePipeline: (name: string, color: string) => Promise<any>;
  onUpdatePipeline: (id: string, updates: Partial<PipelineTab>) => Promise<any>;
  onDeletePipeline: (id: string) => Promise<boolean>;
  onManagePhases?: (pipelineId: string) => void;
  // v1.1.0 - Controle de visibilidade do botão "Novo Pipeline" via permissão granular
  // `crm_pipelines.can_create`. Default true preserva compatibilidade.
  canCreate?: boolean;
  // v1.2.0 - Callback de reordenação das abas. Quando ausente, o drag fica desabilitado.
  onReorderPipelines?: (pipelineIds: string[]) => Promise<any> | void;
}

/**
 * Aba individual sortable (v1.3.0)
 * O arraste é iniciado pela alça de 6 pontinhos (GripVertical), mantendo o
 * clique na aba exclusivo para selecionar o pipeline.
 */
function SortablePipelineTab({
  pipeline,
  children,
  disabled,
}: {
  pipeline: PipelineTab;
  children: React.ReactNode;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pipeline.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
      {...attributes}
    >
      {/* Alça de arraste (mesmo padrão visual das colunas e cards) */}
      {!disabled && (
        <button
          type="button"
          aria-label="Arraste para reordenar o pipeline"
          title="Arraste para reordenar"
          className={cn(
            'absolute left-1 top-1/2 -translate-y-1/2 z-10',
            'text-muted-foreground/40 hover:text-foreground',
            'cursor-grab active:cursor-grabbing transition-colors'
          )}
          onClick={(e) => e.stopPropagation()}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      {children}
    </div>
  );
}


/**
 * Componente de Tabs para navegação entre pipelines CRM
 */
export function DealPipelineTabs({
  pipelines,
  selectedPipelineId,
  onSelectPipeline,
  onCreatePipeline,
  onUpdatePipeline,
  onDeletePipeline,
  onManagePhases,
  canCreate = true,
  onReorderPipelines,
}: DealPipelineTabsProps) {
  // Ordem local das abas (permite feedback otimista durante o drag)
  const [orderedPipelines, setOrderedPipelines] = useState<PipelineTab[]>(pipelines);

  // Sincroniza com a lista vinda do servidor
  useEffect(() => {
    setOrderedPipelines(pipelines);
  }, [pipelines]);

  // Sensores de drag (clique só vira arraste após 6px de deslocamento)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Aplica nova ordem e persiste
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderPipelines) return;

    const oldIndex = orderedPipelines.findIndex(p => p.id === active.id);
    const newIndex = orderedPipelines.findIndex(p => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const previous = orderedPipelines;
    const nova = arrayMove(orderedPipelines, oldIndex, newIndex);
    setOrderedPipelines(nova);

    try {
      await onReorderPipelines(nova.map(p => p.id));
    } catch {
      // Reverte em caso de falha (o toast de erro vem da mutation)
      setOrderedPipelines(previous);
    }
  };


  // Estados dos dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<PipelineTab | null>(null);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [newPipelineColor, setNewPipelineColor] = useState(PRESET_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Abre dialog de edição
  const handleOpenEdit = (pipeline: PipelineTab) => {
    setEditingPipeline(pipeline);
    setNewPipelineName(pipeline.name);
    setNewPipelineColor(pipeline.color || PRESET_COLORS[0]);
    setShowEditDialog(true);
  };

  // Abre dialog de exclusão
  const handleOpenDelete = (pipeline: PipelineTab) => {
    setEditingPipeline(pipeline);
    setShowDeleteDialog(true);
  };

  // Cria pipeline
  const handleCreate = async () => {
    if (!newPipelineName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onCreatePipeline(newPipelineName.trim(), newPipelineColor);
      setNewPipelineName('');
      setNewPipelineColor(PRESET_COLORS[0]);
      setShowCreateDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Atualiza pipeline
  const handleUpdate = async () => {
    if (!editingPipeline || !newPipelineName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onUpdatePipeline(editingPipeline.id, {
        name: newPipelineName.trim(),
        color: newPipelineColor,
      });
      setShowEditDialog(false);
      setEditingPipeline(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Exclui pipeline
  const handleDelete = async () => {
    if (!editingPipeline || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onDeletePipeline(editingPipeline.id);
      setShowDeleteDialog(false);
      setEditingPipeline(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      {/* Faixa rolável (v1.1.0): barra + setas + touchpad quando há muitos pipelines */}
      <ScrollableTabsBar activeKey={selectedPipelineId} className="min-w-0 flex-1">
        <Tabs value={selectedPipelineId || ''} onValueChange={onSelectPipeline}>
          <TabsList className="h-9 w-max">

          {/* v1.2.0 - Abas reordenáveis por drag and drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedPipelines.map(p => p.id)}
              strategy={horizontalListSortingStrategy}
            >
          {orderedPipelines.map((pipeline) => (
            <SortablePipelineTab
              key={pipeline.id}
              pipeline={pipeline}
              disabled={!onReorderPipelines}
            >

              <TabsTrigger
                value={pipeline.id}
                className={cn(
                  'px-3 sm:px-4 pr-7 sm:pr-8 text-xs sm:text-sm whitespace-nowrap',
                  // Espaço extra à esquerda para a alça de arraste
                  onReorderPipelines && 'pl-6 sm:pl-7'
                )}
                style={{ 
                  borderBottomColor: selectedPipelineId === pipeline.id 
                    ? (pipeline.color || PRESET_COLORS[0]) 
                    : 'transparent',
                  borderBottomWidth: 2,
                }}
              >
                <div 
                  className="w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: pipeline.color || PRESET_COLORS[0] }}
                />
                {pipeline.name}
              </TabsTrigger>
              
              {/* Menu de opções do pipeline */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6',
                      'opacity-0 group-hover:opacity-100 transition-opacity'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleOpenEdit(pipeline)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                  
                  {/* Gerenciar fases (se callback fornecido) */}
                  {onManagePhases && (
                    <DropdownMenuItem onClick={() => onManagePhases(pipeline.id)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Gerenciar Fases
                    </DropdownMenuItem>
                  )}
                  
                  {/* Esconde opção de excluir para pipeline padrão */}
                  {!pipeline.is_default && (
                    <DropdownMenuItem 
                      onClick={() => handleOpenDelete(pipeline)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  )}
                  
                  {/* Mostra indicador de item protegido */}
                  {pipeline.is_default && (
                    <DropdownMenuItem disabled className="text-muted-foreground">
                      <Lock className="h-4 w-4 mr-2" />
                      Pipeline protegido
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </SortablePipelineTab>
          ))}
            </SortableContext>
          </DndContext>
        </TabsList>
        </Tabs>
      </ScrollableTabsBar>



      {/* Botão Novo Pipeline (oculto sem permissão `crm_pipelines.can_create`) */}
      {canCreate && (
        <Button
          variant="outline"
          size="sm"
          className="h-9 px-2 sm:px-3 shrink-0"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Novo Pipeline</span>
        </Button>
      )}

      {/* Dialog Criar Pipeline */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Vendas, Renovações..."
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      newPipelineColor === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewPipelineColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={!newPipelineName.trim() || isSubmitting}
            >
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Pipeline */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      newPipelineColor === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewPipelineColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={!newPipelineName.trim() || isSubmitting}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Excluir pipeline "{editingPipeline?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todas as fases e negócios deste pipeline serão excluídos permanentemente.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
              disabled={isSubmitting}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
